/**
 * Manual WebRTC P2P connection fallback.
 * When Trystero's public signaling is unavailable (blocked DHT, restrictive NAT, etc.),
 * users can fall back to a copy/paste (or QR) exchange of WebRTC SDP offers/answers and ICE candidates.
 *
 * This module bundles the SDP and all ICE candidates into a single JSON blob so users only need to
 * exchange one offer and one answer, rather than a flurry of individual candidates.
 *
 * Limitations:
 * - No reconnection support. If the peer disconnects, the user must create a new manual connection.
 * - Requires both sides to be online and able to exchange SDPs synchronously.
 */

import type { P2PMessage } from "../engine/types";
import type { IP2PConnection, ConnectionState, ConnectionEvents } from "./base";
import { serializeMessage, deserializeMessage } from "./protocol";
import { RTC_CONFIG } from "../config/constants";

export type SignalingState =
  | "idle"            // Before offer/answer exchange
  | "offer-created"   // Host has created an offer, waiting to share it
  | "offer-set"       // Guest has set the offer, created answer, waiting to share it
  | "answer-set"      // Host has set the answer, waiting for ICE
  | "connecting"      // ICE exchange in progress
  | "connected"       // Data channel open
  | "error";

export type ManualSignalingEvents = {
  /** Called with the bundled offer (SDP + ICE candidates) ready to share. */
  onOfferCreated?: (offer: string) => void;
  /** Called with the bundled answer (SDP + ICE candidates) ready to share. */
  onAnswerCreated?: (answer: string) => void;
  /** Called whenever a new ICE candidate is gathered (legacy). */
  onIceCandidate?: (candidate: string) => void;
  /** Called whenever the bundled ICE candidate list changes. */
  onIceBundle?: (bundle: string) => void;
};

export type Bundle = {
  type: "offer" | "answer";
  sdp: string;
  candidates: RTCIceCandidateInit[];
};

export class ManualConnection implements IP2PConnection {
  private events: ConnectionEvents;
  private signalingEvents: ManualSignalingEvents;
  private state: ConnectionState = "disconnected";
  private signalingState: SignalingState = "idle";
  private pc: RTCPeerConnection | null = null;
  private dc: RTCDataChannel | null = null;
  private pendingCandidates: RTCIceCandidateInit[] = [];
  private iceCandidates: RTCIceCandidateInit[] = [];
  private iceGatheringComplete = false;

  constructor(
    events: ConnectionEvents,
    signalingEvents: ManualSignalingEvents = {},
  ) {
    this.events = events;
    this.signalingEvents = signalingEvents;
  }

  getState(): ConnectionState {
    return this.state;
  }

  getPeerIds(): string[] {
    return this.dc?.readyState === "open" ? ["manual-peer"] : [];
  }

  getSignalingState(): SignalingState {
    return this.signalingState;
  }

  /** Create an offer (host only). Returns a bundled JSON string with SDP + ICE candidates. */
  async createOffer(): Promise<string> {
    this.setSignalingState("offer-created");
    this.pc = this.createPeerConnection();
    this.dc = this.pc.createDataChannel("pbs-messages", { ordered: true });
    this.setupDataChannel(this.dc);

    const offer = await this.pc.createOffer();
    await this.pc.setLocalDescription(offer);
    return this.waitForIceThenReturnBundle("offer");
  }

  /** Set a remote offer and create an answer (guest only). Returns a bundled JSON string. */
  async acceptOffer(offerJson: string): Promise<string> {
    this.setSignalingState("offer-set");
    this.pc = this.createPeerConnection();

    this.pc.ondatachannel = (event) => {
      this.dc = event.channel;
      this.setupDataChannel(this.dc);
    };

    const bundle = this.parseBundle(offerJson);
    await this.pc.setRemoteDescription({ type: "offer", sdp: bundle.sdp });

    // Add any ICE candidates bundled with the offer
    for (const candidate of bundle.candidates) {
      await this.pc.addIceCandidate(candidate).catch(() => { /* ignore invalid candidates */ });
    }
    // Add candidates that arrived out-of-order before the offer was set
    for (const candidate of this.pendingCandidates) {
      await this.pc.addIceCandidate(candidate).catch(() => { /* ignore invalid candidates */ });
    }
    this.pendingCandidates = [];

    const answer = await this.pc.createAnswer();
    await this.pc.setLocalDescription(answer);
    return this.waitForIceThenReturnBundle("answer");
  }

  /** Set the remote answer (host only). Accepts a bundled JSON string. */
  async acceptAnswer(answerJson: string): Promise<void> {
    if (!this.pc) throw new Error("No peer connection. Create an offer first.");
    this.setSignalingState("answer-set");
    const bundle = this.parseBundle(answerJson);
    await this.pc.setRemoteDescription({ type: "answer", sdp: bundle.sdp });

    for (const candidate of bundle.candidates) {
      await this.pc.addIceCandidate(candidate).catch(() => { /* ignore invalid candidates */ });
    }
    for (const candidate of this.pendingCandidates) {
      await this.pc.addIceCandidate(candidate).catch(() => { /* ignore invalid candidates */ });
    }
    this.pendingCandidates = [];
  }

  /**
   * Add ICE candidates received from the peer.
   * Accepts either a single candidate JSON string or a bundled JSON string.
   */
  async addIceCandidate(candidateJson: string): Promise<void> {
    if (!this.pc) {
      // Try to parse as a bundle and queue all candidates until the peer connection is ready
      const candidates = this.tryParseCandidates(candidateJson);
      this.pendingCandidates.push(...candidates);
      return;
    }
    const candidates = this.tryParseCandidates(candidateJson);
    for (const candidate of candidates) {
      await this.pc.addIceCandidate(candidate).catch(() => { /* ignore invalid candidates */ });
    }
  }

  async send(msg: P2PMessage): Promise<void> {
    if (!this.dc || this.dc.readyState !== "open") throw new Error("Data channel not open");
    this.dc.send(serializeMessage(msg));
  }

  async broadcast(msg: P2PMessage): Promise<void> {
    // Manual connection is point-to-point; broadcast is equivalent to send.
    await this.send(msg);
  }

  async leave(): Promise<void> {
    this.dc?.close();
    this.pc?.close();
    this.dc = null;
    this.pc = null;
    this.pendingCandidates = [];
    this.iceCandidates = [];
    this.iceGatheringComplete = false;
    this.setState("disconnected");
    this.setSignalingState("idle");
  }

  private createPeerConnection(): RTCPeerConnection {
    const pc = new RTCPeerConnection(RTC_CONFIG);
    console.log("[ManualConnection] creating RTCPeerConnection with ICE servers:", RTC_CONFIG.iceServers?.map((s) => s.urls).join(", "));

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        const candidateJson = JSON.stringify(event.candidate.toJSON());
        console.log(`[ManualConnection] ICE candidate: ${event.candidate.candidate}`);
        this.iceCandidates.push(event.candidate.toJSON());
        this.signalingEvents.onIceCandidate?.(candidateJson);
        this.signalingEvents.onIceBundle?.(this.buildBundleJson());
      } else {
        console.log("[ManualConnection] ICE gathering complete");
        this.iceGatheringComplete = true;
        this.signalingEvents.onIceBundle?.(this.buildBundleJson());
      }
    };

    pc.onicegatheringstatechange = () => {
      console.log(`[ManualConnection] ICE gathering state -> ${pc.iceGatheringState}`);
    };

    pc.oniceconnectionstatechange = () => {
      console.log(`[ManualConnection] ICE connection state -> ${pc.iceConnectionState}`);
    };

    pc.onconnectionstatechange = () => {
      console.log(`[ManualConnection] connection state -> ${pc.connectionState}`);
      switch (pc.connectionState) {
        case "connecting":
          this.setState("connecting");
          break;
        case "connected":
          this.setState("connected");
          this.events.onPeerJoin("manual-peer");
          break;
        case "disconnected":
        case "failed":
        case "closed":
          this.setState("disconnected");
          this.events.onPeerLeave("manual-peer");
          break;
      }
    };

    return pc;
  }

  private setupDataChannel(dc: RTCDataChannel): void {
    dc.onopen = () => {
      this.setSignalingState("connected");
      this.setState("connected");
      this.events.onPeerJoin("manual-peer");
    };

    dc.onclose = () => {
      this.setState("disconnected");
      this.events.onPeerLeave("manual-peer");
    };

    dc.onmessage = (event) => {
      const msg = deserializeMessage(event.data);
      if (msg) {
        this.events.onMessage(msg, "manual-peer");
      }
    };
  }

  private buildBundleJson(): string {
    if (!this.pc || !this.pc.localDescription) {
      return JSON.stringify({ type: this.signalingState === "offer-created" ? "offer" : "answer", sdp: "", candidates: [] });
    }
    return JSON.stringify({
      type: this.pc.localDescription.type,
      sdp: this.pc.localDescription.sdp,
      candidates: this.iceCandidates,
    } as Bundle);
  }

  private parseBundle(json: string): Bundle {
    try {
      const parsed = JSON.parse(json) as Partial<Bundle>;
      if (typeof parsed.sdp === "string" && Array.isArray(parsed.candidates)) {
        return { type: parsed.type ?? "offer", sdp: parsed.sdp, candidates: parsed.candidates };
      }
    } catch {
      // fall through to legacy parsing
    }
    // Legacy: treat as raw session description JSON
    const desc = JSON.parse(json) as RTCSessionDescriptionInit;
    return { type: desc.type as "offer" | "answer", sdp: desc.sdp ?? "", candidates: [] };
  }

  private tryParseCandidates(json: string): RTCIceCandidateInit[] {
    try {
      const parsed = JSON.parse(json) as Partial<Bundle> | RTCIceCandidateInit;
      if (Array.isArray((parsed as Partial<Bundle>).candidates)) {
        return (parsed as Bundle).candidates;
      }
      // Single candidate object
      if ("candidate" in parsed || "sdpMid" in parsed || "sdpMLineIndex" in parsed) {
        return [parsed as RTCIceCandidateInit];
      }
    } catch {
      // ignore invalid JSON
    }
    return [];
  }

  private async waitForIceThenReturnBundle(_type: "offer" | "answer"): Promise<string> {
    if (!this.pc || !this.pc.localDescription) {
      throw new Error("Peer connection not initialized");
    }
    if (this.iceGatheringComplete) {
      return this.buildBundleJson();
    }
    return new Promise((resolve, reject) => {
      if (!this.pc) return reject(new Error("Peer connection lost"));
      const pc = this.pc;
      const checkComplete = () => {
        if (pc.iceGatheringState === "complete") {
          pc.onicegatheringstatechange = null;
          this.iceGatheringComplete = true;
          resolve(this.buildBundleJson());
        }
      };
      pc.onicegatheringstatechange = checkComplete;
      checkComplete();
      // Fallback timeout: if ICE takes too long, return whatever we have
      setTimeout(() => {
        if (!this.iceGatheringComplete) {
          pc.onicegatheringstatechange = null;
          this.iceGatheringComplete = true;
          resolve(this.buildBundleJson());
        }
      }, 10000);
    });
  }

  private setState(state: ConnectionState): void {
    this.state = state;
    this.events.onStateChange(state);
  }

  private setSignalingState(state: SignalingState): void {
    this.signalingState = state;
  }
}
